import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, LogOut, LayoutDashboard, ShieldCheck, User as UserIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { NAV_LINKS } from "../../data/mockData";
import { cn } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";

const LOGO = "https://customer-assets.emergentagent.com/job_6a9ec03d-39e5-4689-929c-7a68b7c62383/artifacts/7bwa7zl1_Yantraksh_Logo.png";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const lightText = scrolled ? "text-foreground" : "text-white";

  return (
    <nav className={cn("fixed top-0 w-full z-50 transition-all duration-300", scrolled ? "glass-nav py-2" : "bg-transparent py-3")}>
      <div className="container mx-auto px-4 flex justify-between items-center gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="navbar-logo-link">
          <div className="bg-white rounded-md px-3 py-1.5 shadow-md ring-1 ring-black/5 transition-transform duration-300 group-hover:scale-105">
            <img src={LOGO} alt="Yantraksh Projects" className={cn("w-auto object-contain transition-all duration-300", scrolled ? "h-14" : "h-[68px]")} />
          </div>
          <span className={cn("hidden xl:block font-display font-bold uppercase tracking-wider leading-tight transition-colors", lightText)}>
            <span className="block text-lg">Yantraksh</span>
            <span className="block text-xs text-gold tracking-[0.2em]">Projects Pvt. Ltd.</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-5 lg:gap-6">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                "text-sm font-medium uppercase tracking-wide transition-colors hover:text-gold relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:bg-gold after:transition-all after:duration-300",
                location.pathname === link.path ? "text-gold after:w-full" : "after:w-0 hover:after:w-full",
                lightText
              )}
            >
              {link.name}
            </Link>
          ))}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="navbar-user-menu" className={cn("flex items-center gap-2 text-sm font-medium uppercase tracking-wide", lightText)}>
                  <span className="h-8 w-8 rounded-full bg-gold text-slate-900 flex items-center justify-center font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || <UserIcon size={16} />}
                  </span>
                  <span className="hidden lg:inline max-w-[100px] truncate">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck size={16} className="mr-2" /> Admin Panel
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard size={16} className="mr-2" /> My Documents
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut size={16} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" data-testid="navbar-login" className={cn("text-sm font-medium uppercase tracking-wide hover:text-gold transition-colors", lightText)}>
                Login
              </Link>
              <Button data-testid="navbar-signup" className="bg-gold hover:bg-gold/90 text-slate-900 rounded-none px-5 font-display tracking-widest uppercase text-xs font-bold" asChild>
                <Link to="/register">Sign Up</Link>
              </Button>
            </div>
          )}
        </div>

        {/* Mobile Nav */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className={lightText}>
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[300px] sm:w-[400px] overflow-y-auto">
              <div className="flex flex-col gap-6 mt-10">
                {NAV_LINKS.map((link) => (
                  <Link key={link.path} to={link.path} className="text-lg font-display uppercase tracking-wider font-bold text-foreground hover:text-primary transition-colors">
                    {link.name}
                  </Link>
                ))}
                <div className="border-t pt-6 flex flex-col gap-3">
                  {user ? (
                    <>
                      <p className="text-sm text-gray-500">Signed in as <span className="font-bold">{user.name}</span></p>
                      <Button variant="outline" className="rounded-none" onClick={() => navigate(user.role === "admin" ? "/admin" : "/dashboard")}>
                        {user.role === "admin" ? "Admin Panel" : "My Documents"}
                      </Button>
                      <Button variant="ghost" className="rounded-none" onClick={handleLogout}>Logout</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" className="rounded-none" asChild><Link to="/login">Login</Link></Button>
                      <Button className="rounded-none bg-gold text-slate-900" asChild><Link to="/register">Sign Up</Link></Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
